import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Card } from 'primereact/card';
import { Chart } from 'primereact/chart';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';

import Menu from '../../../components/Menu';
import Loading from '../../../components/Loading';
import api from '../../../services/api';
import logo from '../../../assets/images/logo.png';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './RelatorioProjecaoProducao.css';

const DEFAULT_PERCENT = 90;

const formatNumber = (value) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '0';
  }
  return Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const formatDateToAPI = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDates = () => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const currentDay = today.getDate();

  if (currentDay <= 15) {
    const start = new Date(currentYear, currentMonth - 1, 16);
    const end = new Date(currentYear, currentMonth, 15);
    return { start, end };
  }

  const start = new Date(currentYear, currentMonth, 16);
  const end = new Date(currentYear, currentMonth + 1, 15);
  return { start, end };
};

export default function RelatorioProjecaoProducao() {
  const navigate = useNavigate();
  const toast = useRef(null);
  const printRef = useRef(null);

  const defaultRange = useMemo(() => getDefaultDates(), []);
  const [dataInicial, setDataInicial] = useState(defaultRange.start);
  const [dataFinal, setDataFinal] = useState(defaultRange.end);
  const [percentual, setPercentual] = useState(DEFAULT_PERCENT);

  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulatedTurmas, setSimulatedTurmas] = useState([]);

  const isInvalidDateRange = useMemo(() => {
    if (!dataInicial || !dataFinal) return false;
    const s = new Date(dataInicial);
    const f = new Date(dataFinal);
    s.setHours(0, 0, 0, 0);
    f.setHours(0, 0, 0, 0);
    return s > f;
  }, [dataInicial, dataFinal]);

  const showError = useCallback((msg) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Erro',
      detail: msg,
      life: 4000,
    });
  }, []);

  const showSuccess = useCallback((msg) => {
    toast.current?.show({
      severity: 'success',
      summary: 'Sucesso',
      detail: msg,
      life: 3000,
    });
  }, []);

  const handleDataInicialChange = (e) => {
    const val = e.value;
    setDataInicial(val);
    if (val && dataFinal) {
      const s = new Date(val);
      const f = new Date(dataFinal);
      s.setHours(0, 0, 0, 0);
      f.setHours(0, 0, 0, 0);
      if (s > f) {
        showError('A data inicial não pode ser maior que a data final.');
      }
    }
  };

  const handleDataFinalChange = (e) => {
    const val = e.value;
    setDataFinal(val);
    if (dataInicial && val) {
      const s = new Date(dataInicial);
      const f = new Date(val);
      s.setHours(0, 0, 0, 0);
      f.setHours(0, 0, 0, 0);
      if (f < s) {
        showError('A data final não pode ser menor que a data inicial.');
      }
    }
  };

  const fetchProjecao = useCallback((dtIni, dtFim, pct, isSilent = false) => {
    const sDate = dtIni !== undefined ? dtIni : dataInicial;
    const fDate = dtFim !== undefined ? dtFim : dataFinal;
    const eff = pct !== undefined ? pct : percentual;

    if (!sDate || !fDate) {
      if (!isSilent) showError('Selecione as datas inicial e final.');
      return;
    }

    const s = new Date(sDate);
    const f = new Date(fDate);
    s.setHours(0, 0, 0, 0);
    f.setHours(0, 0, 0, 0);

    if (s > f) {
      if (!isSilent) showError('A data inicial não pode ser maior que a data final.');
      return;
    }

    setLoading(true);
    const params = {
      data_inicial: formatDateToAPI(sDate),
      data_final: formatDateToAPI(fDate),
      percentual: eff,
    };

    api.get('/relatorios-novos/projecao-producao', { params })
      .then((response) => {
        const data = response?.data || null;
        setReportData(data);
        if (data?.turmas) {
          setSimulatedTurmas(JSON.parse(JSON.stringify(data.turmas)));
        }
        if (!isSilent) {
          showSuccess('Relatório de projeção carregado com sucesso.');
        }
      })
      .catch((error) => {
        showError(error?.response?.data?.message || 'Não foi possível carregar a projeção de produção.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dataInicial, dataFinal, percentual, showError, showSuccess]);

  useEffect(() => {
    fetchProjecao(defaultRange.start, defaultRange.end, DEFAULT_PERCENT, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recálculo dinâmico se o usuário estiver simulando dias
  const activeTurmas = useMemo(() => {
    if (!simulatedTurmas || simulatedTurmas.length === 0) return [];
    const metaDia = Number(reportData?.meta_dia_turma || 0);

    return simulatedTurmas.map((turma) => {
      const diasProj = Number(turma.dias_projetados || 0);
      const diasTrab = Number(turma.dias_trabalhados || 0);
      const real = Number(turma.metros_real || 0);

      const metaMes = Math.round(diasProj * metaDia);
      const metaTrab = Math.round(diasTrab * metaDia);
      const mediaDiaReal = diasTrab > 0 ? Math.round(real / diasTrab) : 0;
      const deficit = real - metaTrab;

      return {
        ...turma,
        meta_mes: metaMes,
        meta_dias_trabalhados: metaTrab,
        media_metros_dia_real: mediaDiaReal,
        deficit_superavit: deficit,
      };
    });
  }, [simulatedTurmas, reportData?.meta_dia_turma]);

  const handleSimulatedFieldChange = (index, field, value) => {
    setSimulatedTurmas((prev) => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        [field]: Number(value || 0),
      };
      return copy;
    });
  };

  const handleResetSimulation = () => {
    if (reportData?.turmas) {
      setSimulatedTurmas(JSON.parse(JSON.stringify(reportData.turmas)));
      setIsSimulating(false);
      showSuccess('Simulação redefinida para os dados originais.');
    }
  };

  // Configuração do Gráfico de Rosca
  const chartData = useMemo(() => {
    if (!activeTurmas || activeTurmas.length === 0) return null;

    const labels = activeTurmas.map((t) => t.turma_letra || t.nome);
    const dataValues = activeTurmas.map((t) => Number(t.metros_real || 0));
    
    // Cores específicas como na imagem (Azul, Laranja, Cinza, Amarelo) ou do banco
    const fallbackColors = ['#3b82f6', '#f97316', '#9ca3af', '#eab308'];
    const backgroundColors = activeTurmas.map((t, idx) => t.hex || fallbackColors[idx % fallbackColors.length]);

    return {
      labels,
      datasets: [
        {
          data: dataValues,
          backgroundColor: backgroundColors,
          borderWidth: 2,
          borderColor: '#1f242d',
          hoverOffset: 6,
        },
      ],
    };
  }, [activeTurmas]);

  const chartOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '48%',
      layout: {
        padding: 6,
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#f3f4f6',
            font: {
              size: 13,
              weight: 'bold',
            },
            padding: 14,
            usePointStyle: true,
            boxWidth: 10,
            generateLabels: (chart) => {
              const data = chart.data;
              if (data?.labels?.length && data?.datasets?.length) {
                return data.labels.map((label, i) => {
                  const meta = chart.getDatasetMeta(0);
                  const style = meta.controller.getStyle(i);
                  const val = data.datasets[0].data[i] || 0;
                  return {
                    text: `${label}: ${formatNumber(val)} m`,
                    fillStyle: style.backgroundColor,
                    strokeStyle: style.borderColor,
                    lineWidth: style.borderWidth,
                    hidden: isNaN(val) || (meta.data[i] && meta.data[i].hidden),
                    index: i,
                    fontColor: '#f3f4f6',
                  };
                });
              }
              return [];
            },
          },
        },
        datalabels: {
          display: (ctx) => {
            const val = ctx.dataset.data?.[ctx.dataIndex] ?? 0;
            return Number(val) > 0;
          },
          color: '#ffffff',
          font: {
            size: 11,
            weight: 'bold',
          },
          formatter: (value) => {
            return `${formatNumber(value)} m`;
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const val = Number(ctx.raw || 0);
              return ` ${ctx.label}: ${formatNumber(val)} m`;
            },
          },
        },
      },
    };
  }, []);

  const cleanDOMForExport = () => {
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    toast.current?.clear?.();
    const overlays = document.querySelectorAll('.p-datepicker, .p-toast, .p-tooltip, .no-print');
    overlays.forEach((el) => {
      el.style.setProperty('display', 'none', 'important');
    });
  };

  const restoreDOMAfterExport = () => {
    const overlays = document.querySelectorAll('.p-datepicker, .p-toast, .p-tooltip, .no-print');
    overlays.forEach((el) => {
      el.style.removeProperty('display');
    });
  };

  const html2canvasOptions = {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
    ignoreElements: (element) => {
      if (!element) return false;
      const classList = element.classList;
      if (!classList) return false;
      return (
        classList.contains('p-datepicker') ||
        classList.contains('p-toast') ||
        classList.contains('p-toast-message') ||
        classList.contains('p-tooltip') ||
        classList.contains('no-print')
      );
    },
  };

  const handleDownloadImage = () => {
    if (loading || !reportData || !printRef.current) return;

    cleanDOMForExport();

    setTimeout(() => {
      html2canvas(printRef.current, html2canvasOptions)
        .then((canvas) => {
          const link = document.createElement('a');
          link.download = `projecao-producao-${formatDateToAPI(dataInicial)}-${formatDateToAPI(dataFinal)}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
        })
        .catch(() => {})
        .finally(() => {
          restoreDOMAfterExport();
        });
    }, 100);
  };

  const handleDownloadPDF = () => {
    if (loading || !reportData || !printRef.current) return;

    cleanDOMForExport();

    setTimeout(() => {
      html2canvas(printRef.current, html2canvasOptions)
        .then((canvas) => {
          const imgData = canvas.toDataURL('image/png');
          const pxToMm = (px) => (px * 25.4) / (96 * 2);
          const pdfWidth = pxToMm(canvas.width);
          const pdfHeight = pxToMm(canvas.height);

          const pdf = new jsPDF({
            orientation: pdfHeight > pdfWidth ? 'portrait' : 'landscape',
            unit: 'mm',
            format: [pdfWidth, pdfHeight],
          });

          pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
          pdf.save(`projecao-producao-${formatDateToAPI(dataInicial)}-${formatDateToAPI(dataFinal)}.pdf`);
        })
        .catch(() => {})
        .finally(() => {
          restoreDOMAfterExport();
        });
    }, 100);
  };

  const handlePrint = () => {
    if (loading || !reportData) return;
    cleanDOMForExport();
    setTimeout(() => {
      window.print();
      restoreDOMAfterExport();
    }, 100);
  };

  const isExportDisabled = loading || !reportData;

  return (
    <section className="projecao-report-page">
      <Toast ref={toast} />
      <div className="no-print">
        <Menu />
      </div>

      <div className="p-p-3 no-print">
        <Card className="projecao-toolbar p-mb-4">
          <div className="p-d-flex p-flex-column p-md-flex-row p-md-ai-center p-jc-between">
            <div>
              <h2 className="p-m-0 p-text-bold">Projeção de Produção</h2>
              <span className="p-text-secondary" style={{ fontSize: '0.9rem' }}>
                Relatório analítico de metas mensais, diárias e produção real
              </span>
            </div>

            <div className="p-d-flex p-flex-wrap p-ai-center p-mt-3 p-md-mt-0">
              <Button
                label="Voltar para Metas"
                icon="pi pi-arrow-left"
                className="p-button-outlined p-button-secondary p-mr-2 p-mb-2"
                onClick={() => navigate('/meta-producao')}
              />
              <Button
                label="PDF"
                icon="pi pi-file-pdf"
                className="p-button-danger p-mr-2 p-mb-2"
                onClick={handleDownloadPDF}
                disabled={isExportDisabled}
              />
              <Button
                label="Imagem"
                icon="pi pi-image"
                className="p-button-success p-mr-2 p-mb-2"
                onClick={handleDownloadImage}
                disabled={isExportDisabled}
              />
              <Button
                label="Imprimir"
                icon="pi pi-print"
                className="p-button-info p-mb-2"
                onClick={handlePrint}
                disabled={isExportDisabled}
              />
            </div>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '16px 0' }} />

          {/* Filtros */}
          <div className="p-fluid p-formgrid p-grid p-ai-end">
            <div className="p-field p-col-12 p-md-3">
              <label htmlFor="dataInicial" className="p-d-block p-mb-1 p-text-bold" style={{ fontSize: '0.85rem' }}>
                Data Inicial:
              </label>
              <Calendar
                id="dataInicial"
                value={dataInicial}
                onChange={handleDataInicialChange}
                maxDate={dataFinal || undefined}
                dateFormat="dd/mm/yy"
                showIcon
              />
              {isInvalidDateRange && (
                <small className="p-error p-d-block p-mt-1">
                  Data inicial maior que final.
                </small>
              )}
            </div>

            <div className="p-field p-col-12 p-md-3">
              <label htmlFor="dataFinal" className="p-d-block p-mb-1 p-text-bold" style={{ fontSize: '0.85rem' }}>
                Data Final:
              </label>
              <Calendar
                id="dataFinal"
                value={dataFinal}
                onChange={handleDataFinalChange}
                minDate={dataInicial || undefined}
                dateFormat="dd/mm/yy"
                showIcon
              />
              {isInvalidDateRange && (
                <small className="p-error p-d-block p-mt-1">
                  Data final menor que inicial.
                </small>
              )}
            </div>

            <div className="p-field p-col-12 p-md-2">
              <label htmlFor="percentual" className="p-d-block p-mb-1 p-text-bold" style={{ fontSize: '0.85rem' }}>
                Eficiência (%):
              </label>
              <Dropdown
                id="percentual"
                value={percentual}
                options={[
                  { label: '85%', value: 85 },
                  { label: '90%', value: 90 },
                  { label: '91%', value: 91 },
                  { label: '92%', value: 92 },
                  { label: '93%', value: 93 },
                  { label: '95%', value: 95 },
                ]}
                onChange={(e) => setPercentual(e.value)}
              />
            </div>

            <div className="p-field p-col-12 p-md-2">
              <Button
                label="Atualizar"
                icon="pi pi-refresh"
                className="p-button-primary"
                onClick={() => {
                  if (!dataInicial || !dataFinal) {
                    showError('Selecione as datas inicial e final.');
                    return;
                  }
                  const s = new Date(dataInicial);
                  const f = new Date(dataFinal);
                  s.setHours(0, 0, 0, 0);
                  f.setHours(0, 0, 0, 0);
                  if (s > f) {
                    showError('A data inicial não pode ser maior que a data final.');
                    return;
                  }
                  fetchProjecao(dataInicial, dataFinal, percentual);
                }}
                loading={loading}
                disabled={isInvalidDateRange || !dataInicial || !dataFinal}
              />
            </div>

            <div className="p-field p-col-12 p-md-2">
              <Button
                label={isSimulating ? 'Resetar Simulação' : 'Simular Dias'}
                icon={isSimulating ? 'pi pi-undo' : 'pi pi-pencil'}
                className={isSimulating ? 'p-button-warning' : 'p-button-outlined'}
                onClick={() => {
                  if (isSimulating) {
                    handleResetSimulation();
                  } else {
                    setIsSimulating(true);
                  }
                }}
              />
            </div>
          </div>
        </Card>
      </div>

      {loading && <Loading />}

      {/* Folha do Relatório Oficial (Layout Idêntico às Imagens) */}
      <div className="report-sheet-container" ref={printRef}>
        {/* Cabeçalho com Logo e Título */}
        <div className="report-header p-d-flex p-ai-center p-jc-between">
          <div style={{ width: '180px' }}>
            <img src={logo} alt="Cambos" className="report-logo" />
          </div>
          <div style={{ flex: 1 }}>
            <h1 className="report-main-title">
              {reportData?.titulo || 'PROJEÇÃO DE PRODUÇÃO'}
            </h1>
          </div>
          <div style={{ width: '180px' }} />
        </div>

        {/* Faixa de Indicadores Gerais (Período, Dias de produção, Meta do mês, Metros totais dia) */}
        <div className="indicators-grid">
          <div className="indicator-card">
            <span className="indicator-badge">
              {reportData?.periodo_formatado || `${formatDateToAPI(dataInicial)} a ${formatDateToAPI(dataFinal)}`}
            </span>
            <span className="indicator-label">Período</span>
          </div>

          <div className="indicator-card">
            <span className="indicator-badge">
              {formatNumber(reportData?.dias_producao)}
            </span>
            <span className="indicator-label">Dias de produção</span>
          </div>

          <div className="indicator-card">
            <span className="indicator-badge">
              {formatNumber(reportData?.total_mes)}
            </span>
            <span className="indicator-label">Meta mês</span>
          </div>

          <div className="indicator-card">
            <span className="indicator-badge">
              {formatNumber(reportData?.metros_totais_dia)}
            </span>
            <span className="indicator-label">Metros totais dia</span>
          </div>
        </div>

        {/* Tabela 1: METROS TURMA POR MÊS */}
        <div className="section-heading">
          <div className="section-heading-line" />
          <h2 className="section-heading-title">METROS TURMA POR MÊS</h2>
          <div className="section-heading-line" />
        </div>

        <table className="report-custom-table">
          <thead>
            <tr>
              <th style={{ width: '22%' }}>Turma</th>
              <th style={{ width: '15%' }}>meta</th>
              <th style={{ width: '15%' }}>Dias projetado</th>
              <th style={{ width: '15%' }}>Dia trabalhados</th>
              <th style={{ width: '15%' }}>{percentual}%</th>
              <th style={{ width: '18%' }}>Real</th>
            </tr>
          </thead>
          <tbody>
            {activeTurmas.map((row, idx) => (
              <tr key={row.id || idx}>
                <td className="text-turma">{row.nome}</td>
                <td className="text-meta">{formatNumber(row.meta_mes)}</td>
                <td>
                  {isSimulating ? (
                    <InputNumber
                      value={row.dias_projetados}
                      onValueChange={(e) => handleSimulatedFieldChange(idx, 'dias_projetados', e.value)}
                      min={0}
                      max={60}
                      inputStyle={{ width: '60px', textAlign: 'center', padding: '2px' }}
                    />
                  ) : (
                    formatNumber(row.dias_projetados)
                  )}
                </td>
                <td>
                  {isSimulating ? (
                    <InputNumber
                      value={row.dias_trabalhados}
                      onValueChange={(e) => handleSimulatedFieldChange(idx, 'dias_trabalhados', e.value)}
                      min={0}
                      max={60}
                      inputStyle={{ width: '60px', textAlign: 'center', padding: '2px' }}
                    />
                  ) : (
                    formatNumber(row.dias_trabalhados)
                  )}
                </td>
                <td>{formatNumber(row.meta_dias_trabalhados)}</td>
                <td>
                  {isSimulating ? (
                    <InputNumber
                      value={row.metros_real}
                      onValueChange={(e) => handleSimulatedFieldChange(idx, 'metros_real', e.value)}
                      min={0}
                      inputStyle={{ width: '90px', textAlign: 'center', padding: '2px' }}
                    />
                  ) : (
                    <span className="text-real-box">{formatNumber(row.metros_real)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Tabela 2: METROS TURMA POR DIA */}
        <div className="section-heading">
          <div className="section-heading-line" />
          <h2 className="section-heading-title">METROS TURMA POR DIA</h2>
          <div className="section-heading-line" />
        </div>

        <table className="report-custom-table">
          <thead>
            <tr>
              <th style={{ width: '34%' }}>Turma</th>
              <th style={{ width: '33%' }}>meta / dia</th>
              <th style={{ width: '33%' }}>Media metros dia real</th>
            </tr>
          </thead>
          <tbody>
            {activeTurmas.map((row, idx) => (
              <tr key={row.id || idx}>
                <td className="text-turma">{row.nome}</td>
                <td className="text-meta">{formatNumber(row.meta_dia)}</td>
                <td>{formatNumber(row.media_metros_dia_real)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="report-footer-note">
          Atualizado em {reportData?.data_atualizacao || new Date().toLocaleDateString('pt-BR')}
        </div>

        {/* Espaçamento entre as seções para impressão e visualização */}
        <div style={{ margin: '40px 0 20px' }}>
          {/* Tabela 3: Metros / Turma (Imagem 3) */}
          <table className="report-table-blue">
            <thead>
              <tr>
                <th colSpan="5" className="blue-main-header">
                  Metros / Turma
                </th>
              </tr>
              <tr>
                <th style={{ width: '20%' }}>Turma</th>
                <th style={{ width: '20%' }}>Dias trabalhados</th>
                <th style={{ width: '20%' }}>meta metros/ dia</th>
                <th style={{ width: '20%' }}>metros real do periodo</th>
                <th style={{ width: '20%' }}>déficit/superávit</th>
              </tr>
            </thead>
            <tbody>
              {activeTurmas.map((row, idx) => (
                <tr key={row.id || idx}>
                  <td style={{ fontWeight: 'bold' }}>{row.turma_letra || row.nome}</td>
                  <td>{formatNumber(row.dias_trabalhados)}</td>
                  <td>{formatNumber(row.meta_dias_trabalhados)}</td>
                  <td>{formatNumber(row.metros_real)}</td>
                  <td className={row.deficit_superavit >= 0 ? 'text-superavit' : 'text-deficit'}>
                    {row.deficit_superavit > 0 ? `+${formatNumber(row.deficit_superavit)}` : formatNumber(row.deficit_superavit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Gráfico Rosca: Objetivo Produção (Imagem 3) */}
          <div className="chart-dark-card">
            <div className="chart-dark-title">Objetivo produção</div>
            <div className="chart-wrapper">
              {chartData && (
                <Chart
                  type="doughnut"
                  data={chartData}
                  options={chartOptions}
                  plugins={[ChartDataLabels]}
                  style={{ width: '300px', height: '280px' }}
                />
              )}
            </div>

            {/* Resumo com os metros de cada turma visível na impressão, PDF e imagem */}
            <div className="p-d-flex p-flex-wrap p-jc-center p-ai-center p-mt-3" style={{ gap: '12px' }}>
              {activeTurmas.map((t, idx) => {
                const fallbackColors = ['#3b82f6', '#f97316', '#9ca3af', '#eab308'];
                const cor = t.hex || fallbackColors[idx % fallbackColors.length];
                return (
                  <div
                    key={t.id || idx}
                    className="turma-metric-pill"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 14px',
                      backgroundColor: '#282e3a',
                      borderRadius: '6px',
                      border: `1.5px solid ${cor}`,
                      fontSize: '0.9rem',
                    }}
                  >
                    <span
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: cor,
                        display: 'inline-block',
                      }}
                    />
                    <span style={{ fontWeight: 600, color: '#f3f4f6' }}>
                      {t.turma_letra || t.nome}:
                    </span>
                    <span style={{ fontWeight: 700, color: '#ffffff' }}>
                      {formatNumber(t.metros_real)} m
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
